import React, { useRef, useState, useEffect } from "react";
import "../Styles/Mp3.css";
import audio1 from "../essets/audio1.mp3";
import audio2 from "../essets/audio2.mp3";
import audio3 from "../essets/audio3.mp3";
import audio4 from "../essets/inraudio.mpeg";

function Mp3() {
  const audioRefs = useRef({});
  const [playingId, setPlayingId] = useState(null);
  const [progress, setProgress] = useState({});

  const songs = [
    {
      id: 1,
      title: "Campaign Song 1",
      audio: audio1,
    },
    {
      id: 2,
      title: "Campaign Song 2",
      audio: audio2,
    },
    {
      id: 3,
      title: "Campaign Song 3",
      audio: audio3,
    },
    {
      id: 4,
      title: "Campaign Song 4",
      audio: audio4,
    },
  ];

  const getAudioEl = (id, src) => {
    if (!audioRefs.current[id]) {
      const audio = new Audio(src);
      audioRefs.current[id] = audio;

      audio.onloadedmetadata = () => {
        setProgress((prev) => ({
          ...prev,
          [id]: {
            current: 0,
            duration: audio.duration,
          },
        }));
      };

      audio.ontimeupdate = () => {
        setProgress((prev) => ({
          ...prev,
          [id]: {
            current: audio.currentTime,
            duration: audio.duration,
          },
        }));
      };

      audio.onended = () => {
        setPlayingId(null);
        setProgress((prev) => ({
          ...prev,
          [id]: { ...prev[id], current: 0 },
        }));
      };
    }
    return audioRefs.current[id];
  };

  const togglePlay = (song) => {
    const audio = getAudioEl(song.id, song.audio);

    // Pause any other playing song
    if (playingId && playingId !== song.id) {
      const prevAudio = audioRefs.current[playingId];
      if (prevAudio) prevAudio.pause();
    }

    if (playingId === song.id) {
      audio.pause();
      setPlayingId(null);
    } else {
      audio.play().catch((err) => console.error("Playback failed:", err));
      setPlayingId(song.id);
    }
  };

  const handleSeek = (song, e) => {
    const audio = audioRefs.current[song.id];
    if (!audio || !audio.duration) return;

    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = clickX / rect.width;
    audio.currentTime = percent * audio.duration;
  };

  const handleDownload = async (song) => {
    try {
      const response = await fetch(song.audio);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${song.title.replace(/\s+/g, "_")}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach((audio) => {
        audio.pause();
        audio.src = "";
      });
    };
  }, []);

  return (
    <section className="mp3page-section">
      <div className="mp3page-container">
        <div className="mp3page-header">
          <h2 className="mp3page-title">MP3 SONGS</h2>
          <div className="mp3page-divider">
            <span className="mp3page-line"></span>
            <span className="mp3page-dot">⚙</span>
            <span className="mp3page-line"></span>
          </div>
          <p className="mp3page-subtitle">
            Listen and download your favorite campaign songs
          </p>
        </div>

        <div className="mp3page-grid">
          {songs.map((song) => {
            const isPlaying = playingId === song.id;
            const songProgress = progress[song.id] || {
              current: 0,
              duration: 0,
            };
            const percentPlayed =
              songProgress.duration > 0
                ? (songProgress.current / songProgress.duration) * 100
                : 0;

            return (
              <div className="mp3page-card" key={song.id}>
                <div className="mp3page-icon-box">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 18V5l12-2v13"
                      stroke="#1a1a1a"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="6" cy="18" r="3" stroke="#1a1a1a" strokeWidth="1.8" />
                    <circle cx="18" cy="16" r="3" stroke="#1a1a1a" strokeWidth="1.8" />
                  </svg>
                </div>

                <div className="mp3page-content">
                  <h4 className="mp3page-card-title">{song.title}</h4>

                  <div className="mp3page-player">
                    <button
                      className="mp3page-play-btn"
                      onClick={() => togglePlay(song)}
                    >
                      {isPlaying ? (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="5" y="4" width="5" height="16" rx="1" />
                          <rect x="14" y="4" width="5" height="16" rx="1" />
                        </svg>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 4l14 8-14 8V4z" />
                        </svg>
                      )}
                    </button>

                    <span className="mp3page-time">
                      {formatTime(songProgress.current)} /{" "}
                      {formatTime(songProgress.duration)}
                    </span>

                    <div
                      className="mp3page-progress-track"
                      onClick={(e) => handleSeek(song, e)}
                    >
                      <div
                        className="mp3page-progress-fill"
                        style={{ width: `${percentPlayed}%` }}
                      />
                      <div
                        className="mp3page-progress-thumb"
                        style={{ left: `${percentPlayed}%` }}
                      />
                    </div>

                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="mp3page-volume-icon">
                      <path
                        d="M5 9v6h4l5 4V5L9 9H5z"
                        stroke="#9ca3af"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                      />
                    </svg>

                    <button className="mp3page-menu-btn" type="button">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="1.6" />
                        <circle cx="12" cy="12" r="1.6" />
                        <circle cx="12" cy="19" r="1.6" />
                      </svg>
                    </button>
                  </div>

                  <button
                    className="mp3page-download-btn"
                    onClick={() => handleDownload(song)}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    DOWNLOAD MP3
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Mp3;